/**
 * Utility functions for formatting date ranges
 */

import { MK_CLUB_TIME_ZONE } from '../constants/core'

/**
 * Helper function to parse date strings as UTC dates (for calendar dates from API)
 * Date strings in YYYY-MM-DD format represent calendar dates and should be parsed as UTC dates
 * at midnight UTC to preserve the calendar date regardless of timezone
 */
const parseUTCDate = (dateStr: string): Date => {
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    // Parse YYYY-MM-DD as UTC date at midnight UTC
    const parts = dateStr.split('-').map(Number)
    if (parts.length === 3) {
      const [year, month, day] = parts
      return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)) // month is 0-indexed, UTC date
    }
  }
  return new Date(dateStr)
}

/**
 * Checks if two dates are in the same month and year
 */
export const areDatesInSameMonthAndYear = (startDateStr: string, endDateStr: string): boolean => {
  const startDate = parseUTCDate(startDateStr)
  const endDate = parseUTCDate(endDateStr)
  return startDate.getUTCMonth() === endDate.getUTCMonth() && startDate.getUTCFullYear() === endDate.getUTCFullYear()
}

/**
 * Checks if two dates are in the same year
 */
export const areDatesInSameYear = (startDateStr: string, endDateStr: string): boolean => {
  const startDate = parseUTCDate(startDateStr)
  const endDate = parseUTCDate(endDateStr)
  return startDate.getUTCFullYear() === endDate.getUTCFullYear()
}

/**
 * Formats a date range string for UI display.
 * Examples:
 * - Same month/year: "Jun 2-8, 2025"
 * - Different months, same year: "Jun 2 - Jul 8, 2025"
 * - Different years: "Jun 2, 2024 - Jul 8, 2025"
 *
 * Dates are treated as calendar dates only - no timezone conversion.
 * For UTC dates (from context), extracts UTC components and creates local Date objects for formatting.
 * This preserves the calendar date that was selected, regardless of timezone.
 */
export const formatDateRange = (startDateSelected: Date | null | undefined, endDateSelected: Date | null | undefined): string => {
  if (!startDateSelected || !endDateSelected) {
    return 'Dates not available'
  }

  const locale = 'en-US'

  try {
    // Extract UTC components from UTC dates (dates from context are UTC dates)
    // Then create local Date objects from those components to preserve the calendar date
    const startYear = startDateSelected.getUTCFullYear()
    const startMonth = startDateSelected.getUTCMonth() // Already 0-indexed
    const startDay = startDateSelected.getUTCDate()

    const endYear = endDateSelected.getUTCFullYear()
    const endMonth = endDateSelected.getUTCMonth() // Already 0-indexed
    const endDay = endDateSelected.getUTCDate()

    // Create local Date objects from UTC components - this preserves the calendar date
    const startDateForFormatting = new Date(startYear, startMonth, startDay)
    const endDateForFormatting = new Date(endYear, endMonth, endDay)

    // Format the local Date objects - this will show the correct calendar date
    const optionsWithoutYear: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
    }

    const optionsWithYear: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }

    const startFormatted = new Intl.DateTimeFormat(locale, optionsWithoutYear).format(startDateForFormatting)
    const endFormatted = new Intl.DateTimeFormat(locale, optionsWithoutYear).format(endDateForFormatting)
    const startFormattedWithYear = new Intl.DateTimeFormat(locale, optionsWithYear).format(startDateForFormatting)
    const endFormattedWithYear = new Intl.DateTimeFormat(locale, optionsWithYear).format(endDateForFormatting)

    if (startYear === endYear && startMonth === endMonth) {
      // Same month/year: "Jun 2-8, 2025"
      return `${startFormatted}-${endDay}, ${startYear}`
    }

    if (startYear === endYear) {
      // Different months, same year: "Jun 2 - Jul 8, 2025"
      return `${startFormatted} - ${endFormatted}, ${startYear}`
    }

    // Different years: "Jun 2, 2024 - Jul 8, 2025"
    return `${startFormattedWithYear} - ${endFormattedWithYear}`
  } catch (error) {
    console.error('Error formatting date range:', error)
    return 'Date formatting error'
  }
}

export const formatDateRangeFromStrings = (startDateStr: string, endDateStr: string): string => {
  // Parse date strings as UTC dates (they represent calendar dates from API)
  // formatDateRange expects UTC dates and will extract UTC components correctly
  const startDate = parseUTCDate(startDateStr)
  const endDate = parseUTCDate(endDateStr)
  return formatDateRange(startDate, endDate)
}

/**
 * Formats a compact "month + day" date range using LOCAL date components and the standard
 * `Intl.DateTimeFormat.formatRange()` output for en-US (locale-correct separator and spacing,
 * identical days collapse to a single date). The year is always stripped — this util is
 * intentionally year-less so callers don't need to post-process the result.
 *
 * Examples:
 * - Same month:     "Apr 6 - 9"
 * - Cross month:    "Apr 21 - May 5"
 * - Cross year:     "Dec 25 - Jan 8"
 * - Identical day:  "May 21"
 *
 * For UTC-stored ranges with a fixed "MMM D, YYYY" format, use {@link formatDateRange} instead.
 */
export const formatShortDateRange = (startDate: Date, endDate: Date): string =>
  new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
    .formatRange(startDate, endDate)
    .replace(/,\s\d{2,4}/g, '')
